import { z } from 'zod';
import { Timestamp, UserRef, Uuid } from './common.js';
import { EntityMergeCandidateSignal, MergeCardRelation } from './decisions.js';
import { AwaitingRunState, PendingDecisionKind, SubjectStatus } from './vocab.js';

/**
 * `GET /needs-action` — the projection over pending decisions plus run states
 * (server-design.md §3.4). Nothing else drives it.
 *
 * The server ranks: blocking → queued → identity kinds. **Order is preserved by
 * the client** — Home renders the array as it arrives and never re-sorts.
 */
/**
 * The run states this projection can carry. See the `state` field below for why
 * it is wider than "the states that need a human".
 */
export const NeedsActionRunState = z.union([
  AwaitingRunState,
  z.enum(['created', 'running', 'checkpointed', 'failed']),
]);
export type NeedsActionRunState = z.infer<typeof NeedsActionRunState>;

export const NeedsActionItem = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('decision'),
    decision_id: Uuid,
    /**
     * `entity_merge_candidate` is **excluded**: it is not a question about a
     * person the office follows, so it has no subject heading to sit under and
     * rides in `merge_candidates` below instead.
     *
     * `entity_rename` is excluded for the same reason and rides in
     * `rename_proposals`. A lookup proposing that a record is really called
     * something else is a question about the record's name, and the record is as
     * often an organisation — which has no status to print under a heading.
     */
    kind: PendingDecisionKind.exclude(['entity_merge_candidate', 'entity_rename']),
    /** `claim_adjudication` only; null on identity kinds. */
    blocking: z.boolean().nullable(),
    /**
     * The run this question was raised on; null when it was raised outside one
     * (a hand-written claim), or on identity kinds.
     *
     * A deliberate extension of what this allow-list projection carries, because
     * since §3f the id is the only thing that separates two rows that otherwise
     * look identical: a `claim_adjudication` on a run parked in
     * `awaiting_adjudication` is **batched at that park** — blocking or queued,
     * the run is waiting on it — while one that outlived its run stays
     * answerable inline on the brief, with nothing waiting on it. Home says
     * those two in different lines, and cannot tell them apart without this.
     */
    run_id: Uuid.nullable(),
  }),
  z.object({
    type: z.literal('run'),
    run_id: Uuid,
    /**
     * `awaiting_*` and `failed` are the states that need the office; `created`,
     * `running` and `checkpointed` need nothing and are carried anyway, for one
     * reason.
     *
     * Home has to place every `claim_adjudication` row it is handed, and since
     * §3f the only thing that separates the two placements is **whether the row's
     * run is still live**: a live run's questions batch at its park and are
     * withheld from the pinned brief until then, while a question that outlived
     * its run stays answerable inline on the brief. A run in the facet pass is
     * live and needs nothing — so without its state on this list Home would offer
     * "N findings to review on the brief" for a row the brief will not show, and
     * the deep link would land the office on a page with nothing on it.
     *
     * A run in these three states is therefore carried **only** when a pending row
     * in the same entry names it (`needs-action.service.ts`), so an entry never
     * appears on the strength of a run that is simply working.
     *
     * `checkpointed` joined them with the narration build: it is a between-legs
     * stop with the successor already enqueued — no human input is awaited — so it
     * is "still working" in every sense this list cares about.
     *
     * An `awaiting_*` run is carried **only while a gating decision on it is
     * still pending**. Answering the last one enqueues the continuation without
     * writing any state, so the run stays `awaiting_*` across dispatch and cold
     * start with nothing left to ask — resuming, not waiting. Home would
     * otherwise keep offering "More detail needed to identify" for a question
     * already answered, and the deep link would land on a page whose form has
     * unmounted.
     */
    state: NeedsActionRunState,
    /**
     * **Whether Home may offer Resume** — true only on a `failed` run the server
     * would resume. An `IDENTITY_MATCH_FAILED` run is failed and still news, but
     * a Resume would fail the same way until Arbiter fixes it, so the server
     * refuses one and Home links to the record, which offers a new quick search
     * (2026-09-29).
     */
    resumable: z.boolean(),
  }),
]);
export type NeedsActionItem = z.infer<typeof NeedsActionItem>;

export const NeedsActionEntry = z.object({
  subject: z.object({
    id: Uuid,
    name: z.string(),
    /**
     * **Which page this subject's rows link to**, and the reason the field
     * exists at all (2026-09-10). Home built every one of its links as
     * `/people/:id/research`, which was true while only people were followed;
     * an organisation's failed lookup then put a row on Home whose link was a
     * **404** with somebody else's noun in it. The client cannot work the kind
     * out from an id, so the server says it.
     *
     * The three kinds a subject can be; an event became one with the event
     * page (2026-09-23), and a question raised by its research links there.
     */
    kind: z.enum(['person', 'org', 'event']),
    /** The org's vertical — which vocabulary `status` is drawn from (§7.9a). */
    vertical: z.string(),
    /**
     * **Null for an organisation** (organisations-as-subjects note §1 R1): a
     * tracked organisation has no status row, because neither vocabulary has a
     * word for one, and it reaches this list the moment a question is asked
     * about it. `.nullable()` rather than `.optional()` is the house rule — the
     * absence is a value the client renders, not a field that may be missing.
     */
    status: SubjectStatus.nullable(),
  }),
  items: z.array(NeedsActionItem),
});
export type NeedsActionEntry = z.infer<typeof NeedsActionEntry>;

/** One side of a likely-merge question, as the card shows it. */
export const NeedsActionMergeSide = z.object({
  entity_id: Uuid,
  name: z.string(),
  connection_count: z.number().int().nonnegative(),
  /** Has a record of their own — "someone you follow". */
  tracked: z.boolean(),
});
export type NeedsActionMergeSide = z.infer<typeof NeedsActionMergeSide>;

/**
 * **What merge triage made of a question it did not settle**
 * (`docs/notes/2026-10-02-merge-triage-and-unmerge-spec.md` §3): the model's
 * reading, or the answer a rule or model arm would have given had it been
 * switched on. Every field is nullable and required.
 *
 * `question`, when present, is printed under the card's heading in place of the
 * name sentence; `proposed_answer` preselects the matching answer and
 * `proposed_reading` the matching relation. `unclear` preselects nothing. No
 * confidence travels with any of it.
 */
export const NeedsActionMergeTriage = z.object({
  /** One plain sentence citing the evidence. */
  reason: z.string().nullable(),
  /** The specific question that would settle it, naming what is on record. */
  question: z.string().nullable(),
  proposed_answer: z.enum(['same', 'different', 'related', 'unclear']).nullable(),
  /** On a `related` proposal between two organizations: the reading, as the card sends it. */
  proposed_reading: z.object({ relation: MergeCardRelation, whole_entity_id: Uuid }).nullable(),
});
export type NeedsActionMergeTriage = z.infer<typeof NeedsActionMergeTriage>;

/**
 * **"Are these the same?"** — a pending `entity_merge_candidate`, listed apart
 * from the per-subject entries (likely-merges note §1f).
 *
 * Apart, because the entries above are grouped under **a person the office
 * follows** and carry that person's status in the org's own vocabulary. The two
 * sides of this question are as often organisations or events, and neither of
 * them need be followed by anybody — so there is no heading to file it under and
 * no status to print, and forcing one would put a node on Home as a Prospect it
 * has never been.
 *
 * `a` is the row that was just minted — the new spelling — and `b` the older row
 * it might be another spelling of. The order is the question's, not a ranking:
 * "Same" merges into whichever of the two carries more of the record, which the
 * server decides at answer time and neither side of this shape declares.
 */
export const NeedsActionMergeCandidate = z.object({
  decision_id: Uuid,
  /** The entity kind both sides are — `person`, `org` or `event`. */
  kind: z.enum(['person', 'org', 'event']),
  a: NeedsActionMergeSide,
  b: NeedsActionMergeSide,
  /** Why they look like the same thing, in one sentence. Rendered verbatim. */
  reason: z.string(),
  /**
   * **What the probe noticed**, which the card does not print and does use
   * (organisation-identity note §4).
   *
   * The office reads `reason`; this is the one thing about the match the card
   * needs as a *word* rather than as a sentence. On a `contains` pair one name
   * is inside the other, so the longer of the two is the fuller spelling and is
   * the name the record should keep unless the office says otherwise — and
   * preselecting it is the difference between a question with an answer already
   * in it and a question nobody wants to be asked. Nothing else on the card
   * reads it: no score, no signal name, no "likely".
   *
   * Nullable, and null is a real answer: a question recorded before the signal
   * was carried on this shape, or a payload without one, has nothing to say
   * here and the card falls back to its other rule.
   */
  signal: EntityMergeCandidateSignal.nullable(),
  /**
   * **What somebody said would settle it** — one sentence written against the
   * open question rather than an answer to it (reconcile-through-the-connector
   * note §2.4).
   *
   * A reconcile pass that cannot settle a pair from the record says so here
   * instead of guessing: *"Which Georgia: the university, the state, or a
   * school?"* The card prints it under the reason. It is not a state — the row
   * stays `pending` and leaves the band when it is answered, from either
   * surface; there is no separate clear. `via` is the surface it was written
   * from, and null is the app. Null here is a question nobody has held.
   */
  needs_info: z
    .object({
      text: z.string(),
      by: UserRef.nullable(),
      via: z.enum(['connector']).nullable(),
      at: Timestamp,
    })
    .nullable(),
  /**
   * **What answering "Same" would do, said before the office says it** — one
   * server-owned sentence, rendered verbatim above the confirm control, and the
   * same sentence the connector reads back
   * (`docs/notes/2026-09-16-fold-tracked-people.md` §7).
   *
   * "Same person" used to be a bare yes about two spellings: the satellite gate
   * refused every fold that would have moved anything somebody had worked on, so
   * the worst a wrong answer could do was redirect a name. Since 2026-09-16 a
   * fold carries a tracked record — it unbinds the losing side's identity
   * versions, supersedes its brief, moves its runs, evidence, notes and status,
   * and changes which id the one record lives at. None of that is visible on a
   * card showing two names and a reason.
   *
   * *"Keeps Patrick Battle and its research; Pat Battle folds in; status becomes
   * member (the other record said prospect)."*
   *
   * Computed **on read**, never stored: the pair moves — a fold elsewhere, a run
   * finishing, a status switched — between the probe writing the question and
   * the office answering it, and a sentence frozen at write time would promise
   * whichever of those was true a week ago. It is the one field on this card
   * that is about the future, so it is the one that can least afford to be
   * stale.
   *
   * **`.nullable()` and not `.optional()`** (the house rule): null is a value the
   * card renders as nothing — either side gone means the answer road will record
   * that instead of folding — not a key that may be missing.
   */
  fold_readback: z.string().nullable(),
  /**
   * What merge triage made of it, or null when triage has not run, did not
   * settle anything worth showing, or failed (merge triage spec §3).
   */
  triage: NeedsActionMergeTriage.nullable(),
  created_at: Timestamp,
});
export type NeedsActionMergeCandidate = z.infer<typeof NeedsActionMergeCandidate>;

/**
 * **One answer merge triage gave without asking**, for the home screen's
 * "Merged automatically" band (merge triage spec §3): the last 30 days of
 * triage answers, merges and "different"s alike, newest first.
 *
 * `sentence` is the whole entry, server-written and rendered verbatim; it names
 * both records and the reason. Undo is one of two calls, and `undo` says which:
 * `unmerge` posts to `/orgs/:orgId/merges/:merge_decision_id/unmerge`, `reopen`
 * to `/orgs/:orgId/merge-triage/:decision_id/undo`. An answer already undone is
 * not listed.
 */
export const NeedsActionTriageAnswer = z.object({
  /** The `entity_merge_candidate` triage answered. */
  decision_id: Uuid,
  kind: z.enum(['person', 'org', 'event']),
  answer: z.enum(['same', 'different']),
  sentence: z.string(),
  /** The `entity_merge` a "same" produced; null on a "different". */
  merge_decision_id: Uuid.nullable(),
  undo: z.enum(['unmerge', 'reopen']),
  answered_at: Timestamp,
});
export type NeedsActionTriageAnswer = z.infer<typeof NeedsActionTriageAnswer>;

/** `POST /orgs/:orgId/merge-triage/:decisionId/undo` — the question filed again. */
export const MergeTriageUndoResponse = z.object({
  /** The fresh pending question, now on the home screen. */
  decision_id: Uuid,
});
export type MergeTriageUndoResponse = z.infer<typeof MergeTriageUndoResponse>;

/**
 * **"This looks like it is really called Pogue Holdings. Rename it?"** — a
 * pending `entity_rename` from a lookup, listed apart from the per-subject
 * entries (organisations-as-subjects note §2 chunk 3a, profile note §1.4).
 *
 * Apart for the merge candidate's reason and one of its own. The entries above
 * are grouped under **a person the office follows** and print that person's
 * status in the org's vocabulary; the record a lookup wants to rename is an
 * organisation, which has no status, and the question is not a finding about
 * anybody.
 *
 * The one of its own: the case this exists for is the five placeholders on prod
 * — "family business", "his own practice", "a new fund" — whose current name is
 * not a name at all. Filing that under a heading reading *family business* would
 * be the card asking the question in the words the question is about.
 *
 * **Denormalised on purpose.** The two names and the sentence are all on the
 * decision's payload, but a client that had to fetch the decision to draw the
 * card would draw an empty one first — and Home renders this list inline. The
 * shape mirrors {@link NeedsActionMergeCandidate}'s for the same reason.
 */
export const NeedsActionRenameProposal = z.object({
  decision_id: Uuid,
  entity_id: Uuid,
  /** `person`, `org` or `event` — the rename is kind-agnostic (R6). */
  entity_kind: z.enum(['person', 'org', 'event']),
  /** What the record goes by now — very often a placeholder somebody typed. */
  from_name: z.string(),
  /** What the run says it is really called. */
  to_name: z.string(),
  /**
   * What the run read that off, in one sentence, rendered verbatim. Nullable
   * because the kind is also what the office's own door files, and a rename
   * somebody typed has no evidence to give — though such a row is filed answered
   * and never reaches this list.
   */
  evidence: z.string().nullable(),
  /** The run that proposed it; null on a row raised outside one. */
  run_id: Uuid.nullable(),
  created_at: Timestamp,
});
export type NeedsActionRenameProposal = z.infer<typeof NeedsActionRenameProposal>;

export const NeedsActionResponse = z.object({
  entries: z.array(NeedsActionEntry),
  /**
   * Same-employer questions first, then oldest first, with a stable tiebreak on
   * the decision id so a batch stamped in one transaction comes back in the same
   * order on every read. Empty is the ordinary case.
   */
  merge_candidates: z.array(NeedsActionMergeCandidate),
  /** The "Merged automatically" band: triage's answers from the last 30 days, newest first. */
  merged_automatically: z.array(NeedsActionTriageAnswer),
  /** Oldest first, and empty until a lookup finds a name. */
  rename_proposals: z.array(NeedsActionRenameProposal),
  /**
   * **Whether the organization has spent its allowance for the month**
   * (2026-09-09), which is the whole of what the office is told about it.
   *
   * Not the limit, not the spend, not how close either is — the office is shown
   * no figure anywhere, because the allowance is the operator's and a number the
   * office cannot change is a question rather than an answer (the same rule that
   * keeps `research` off `OrgSettingsView`). What the office needs is the fact
   * that new work is being refused and the one move it has: ask.
   *
   * **On this read rather than on `GET /me` or the settings GET**, and the
   * choice is about what is fetched when. The settings read happens on the
   * settings page alone and is not polled, so a banner hung off it would never
   * appear on Home. `/me` is org-less and carries every org the caller belongs
   * to, so the flag there would be one aggregate query per org on every poll for
   * a banner that renders on one route. This read *is* that route's read — one
   * org, once a page, re-polled on the page interval — so the banner is correct
   * within a poll and costs one indexed sum.
   *
   * Required and never `.optional()`: false is a real answer and an absent key
   * would make an older server read as an organization under its limit.
   */
  monthly_spend_ceiling_reached: z.boolean(),
});
export type NeedsActionResponse = z.infer<typeof NeedsActionResponse>;
